import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookMarked, ScrollText, ChevronDown, ExternalLink } from "lucide-react";
import { PRAYERS } from "./data";
import { DogmaPreviews } from "@/components/dogmas/DogmaPreviews";
import { Reveal, SectionHeading, EASE } from "./Reveal";
import { PrayerAudio } from "@/components/prayers/PrayerAudio";

function AccordionItem({ item, open, onToggle, testid }) {
  return (
    <div className="border-b border-marian/10 last:border-b-0">
      <button
        type="button"
        data-testid={testid}
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
      >
        <span>
          <span className="block font-serif text-lg font-semibold text-midnight sm:text-xl">
            {item.title}
          </span>
          {item.seal && (
            <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
              {item.seal}
            </span>
          )}
        </span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-marian transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="max-w-xl whitespace-pre-line break-words pb-6 text-base leading-relaxed text-charcoal/85" data-testid={`${testid}-text`}>{item.text}</p>
            <PrayerAudio prayerId={item.id} prefix={testid} lang="en" />
            {item.sourceUrl && <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-source`} className="mb-6 inline-flex items-center gap-2 text-xs font-semibold text-marian underline underline-offset-4 transition-colors hover:text-gold">{item.sourceLabel}<ExternalLink size={12} aria-hidden="true" /></a>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function LearnAndPray() {
  const [tab, setTab] = useState("dogmas");
  const [openPrayer, setOpenPrayer] = useState(0);

  return (
    <section id="pray" data-testid="learn-and-pray" className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHeading eyebrow="Learn and pray" title="Doctrine to ponder, prayers to keep">
          Whether you are beginning the Rosary, preparing for a Marian feast, or simply asking
          the Mother of Jesus to pray for you — begin here.
        </SectionHeading>

        <div className="mt-12">
          <Reveal>
            <div>
              <div className="flex max-w-md gap-2 rounded-full bg-paleblue p-1" role="tablist" aria-label="Learn and pray">
                {[
                  { id: "dogmas", label: "The four dogmas", icon: BookMarked },
                  { id: "prayers", label: "Marian prayers", icon: ScrollText },
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.id}
                    data-testid={`tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                      tab === t.id
                        ? "bg-marian text-ivory shadow"
                        : "text-marian hover:bg-white/70"
                    }`}
                  >
                    <t.icon className="h-4 w-4" aria-hidden="true" />
                    <span className="hidden sm:inline">{t.label}</span>
                    <span className="sm:hidden">{t.id === "dogmas" ? "Dogmas" : "Prayers"}</span>
                  </button>
                ))}
              </div>

              <div className={`mt-8 ${tab === "prayers" ? "max-w-3xl" : ""}`}>
                {tab === "dogmas"
                  ? <DogmaPreviews />
                  : PRAYERS.map((prayer, i) => (
                      <AccordionItem
                        key={prayer.title}
                        item={prayer}
                        open={openPrayer === i}
                        onToggle={() => setOpenPrayer(openPrayer === i ? -1 : i)}
                        testid={`prayer-${i}`}
                      />
                    ))}
              </div>
            </div>
          </Reveal>

        </div>
      </div>
    </section>
  );
}
